import { useState, useEffect, useRef } from "react";
import Hark from "hark";
import { v4 as uuidv4 } from "uuid";

// https://cloud.google.com/speech-to-text/docs/reference/rest/v1/RecognitionConfig
import { GoogleCloudRecognitionConfig } from "./GoogleCloudRecognitionConfig";
import { useRecorderPermission } from "./recordPermissions";
import { flatten, isEmpty } from "lodash";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { updateInternetStatus } from "@store/internetConnectionSlice";
import { InternetStatus, SttSourceType } from "@store/generalTypes";

const AudioContext = window.AudioContext || (window as any).webkitAudioContext;

export interface ResultType {
    speechBlob?: Blob;
    elapsed: number;
    transcript: string;
    confidence: number;
    intendedWord?: string;
    context?: any;
    source?: SttSourceType;
}

export interface UseSpeechToTextTypes {
    continuous?: boolean;
    fullAudio?: boolean;
    crossBrowser?: boolean;
    googleApiKey: string;
    googleCloudRecognitionConfig?: GoogleCloudRecognitionConfig;
    azureApiKey: string;
    azureRegion: string;
    onStartSpeaking?: () => any;
    onStoppedSpeaking?: () => any;
    timeout?: number;
    useLegacyResults?: boolean;
    useOnlyGoogleCloud?: boolean;
    threshold?: number;
    stoppedTalkingThreshold?: number;
    testOnly?: boolean;
    useStopTalking?: boolean;
}

export interface UseSpeechToTextReturnTypes {
    error: string;
    interimResult?: string;
    isRecording: boolean;
    results: ResultType[];
    startSpeechToText: (intendedWord?: string, explicitContext?: string[], context?: any) => Promise<void>;
    stopSpeechToText: (
        processAudio?: boolean,
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ) => Promise<void>;
    destroy: () => void;
    recordingVolume?: number;
    currentDecibels: number;
    stream?: MediaStream;
}

interface ITransaction {
    assessmentId: number;
    assessmentFlow: string;
    payloadStt: any;
    statusCode: number;
    statusText: string;
    responseStt: string;
}

export const useSpeechToText = ({
    fullAudio,
    googleApiKey,
    googleCloudRecognitionConfig,
    azureApiKey,
    azureRegion,
    onStartSpeaking,
    onStoppedSpeaking,
    threshold = 0.8,
    stoppedTalkingThreshold = -60,
    testOnly,
    useStopTalking = true
}: UseSpeechToTextTypes): UseSpeechToTextReturnTypes => {
    const [isRecording, setIsRecording] = useState(false);

    const audioContextRef = useRef<AudioContext>();

    const [recorder, stream] = useRecorderPermission();

    const [results, setResults] = useState<ResultType[]>([]);

    const [error, setError] = useState("");

    const [currentDecibels, setCurrentDecibels] = useState(-100);

    const [vol, setVol] = useState<number>();
    const mediaStream = useRef<MediaStream>();

    const dispatch = useAppDispatch();

    const appInsights = useAppInsightsContext();

    const { assessmentFlow, assessment, globalSettings } = useAppSelector((x) => {
        return { assessmentFlow: x.assessmentFlow, assessment: x.assessment, globalSettings: x.globalSettings };
    });

    useEffect(() => {
        if (!navigator?.mediaDevices?.getUserMedia) {
            setError("getUserMedia is not supported on this device/browser :(");
        }

        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContext();
        }
    }, []);

    const startSpeechToText = async (
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ): Promise<void> => {
        setIsRecording(true);

        await recorder?.reset();
        void recorder?.startRecording();

        mediaStream.current = stream?.clone();

        if (audioContextRef.current?.state === "suspended") {
            void audioContextRef.current?.resume();
        }

        const speechEvents = Hark(mediaStream.current!, {
            audioContext: audioContextRef.current as AudioContext,
            threshold: stoppedTalkingThreshold
        });

        handleVolume();

        speechEvents.on("speaking", () => {
            if (onStartSpeaking) onStartSpeaking();
        });

        if (testOnly) {
            speechEvents.on("volume_change", (c) => {
                setCurrentDecibels(c);
            });
        }
        if (useStopTalking) {
            speechEvents.on("stopped_speaking", async () => {
                if (testOnly) return;
                if (onStoppedSpeaking) onStoppedSpeaking();
                try {
                    await recorder?.stopRecording();
                    const blob = await recorder?.getBlob();
                    handleBlobToBase64({
                        blob,
                        reset: fullAudio,
                        intendedWord,
                        explicitContext,
                        context
                    });
                    stopMediaStream();
                } catch (er) {
                    appInsights.trackTrace({
                        message: JSON.stringify({
                            text: "error startSpeechToText stopped_speaking",
                            context,
                            er
                        }),
                        severityLevel: SeverityLevel.Information
                    });

                    console.error(er);
                }
            });
        }
    };

    const stopSpeechToText = async (
        processAudio?: boolean,
        intendedWord?: string,
        explicitContext?: string[],
        context?: any
    ): Promise<void> => {
        try {
            setIsRecording(false);

            stopMediaStream();

            const recorderState = await recorder?.getState();

            if (recorderState !== "inactive") {
                await recorder?.stopRecording();
            }

            const blob = await recorder?.getBlob();
            await recorder?.reset();
            if (processAudio) {
                handleBlobToBase64({
                    blob,
                    reset: false,
                    intendedWord,
                    explicitContext,
                    context
                });
            }
        } catch (er) {
            appInsights.trackTrace({
                message: JSON.stringify({
                    text: "error stopSpeechToText",
                    context,
                    er
                }),
                severityLevel: SeverityLevel.Information
            });

            console.error(er);
        }
    };

    const handleBlobToBase64 = ({
        blob,
        reset = false,
        intendedWord,
        explicitContext = [],
        context = {}
    }: {
        blob?: Blob;
        reset?: boolean;
        intendedWord?: string;
        explicitContext?: string[];
        context?: any;
    }): void => {
        if (!blob || blob.size < 1 || testOnly) return;

        const reader = new FileReader();
        reader.readAsDataURL(blob);

        reader.onloadend = async () => {
            const start = Date.now();

            const base64data = reader.result as string;

            const audio = { content: "" };

            const config: GoogleCloudRecognitionConfig = {
                encoding: "LINEAR16",
                languageCode: "en-US",
                sampleRateHertz: 16000,
                ...googleCloudRecognitionConfig
            };

            const languageCode = config.languageCode ?? "en-US";

            if (explicitContext.length > 0) {
                let boost = 10;
                if (!isEmpty(googleCloudRecognitionConfig?.speechContexts)) {
                    boost = Number(googleCloudRecognitionConfig?.speechContexts![0].boost);
                }

                config.speechContexts = [{ boost, phrases: explicitContext }];
            }

            const data = {
                config,
                audio
            };

            // Gets raw base 64 string data
            audio.content = base64data.substring(base64data.indexOf(",") + 1);

            if (reset) {
                stopMediaStream();
                void startSpeechToText(intendedWord, explicitContext, context);
            }

            // create app insight log
            const currentFlow = assessmentFlow.currentFlow - 1;
            const flowName = currentFlow >= 0 ? assessmentFlow.flows[currentFlow].name : "Practice";

            const transaction = {
                assessmentId: assessment.id,
                assessmentFlow: flowName,
                payloadStt: data,
                statusCode: 0,
                statusText: "",
                responseStt: "",
                azureResponseStt: ""
            };
            try {
                // create fetch crontroller
                let timeout = 100 * 1000;

                if (flowName.includes("Present Targets")) {
                    timeout = globalSettings.ptTimeout * 1000;
                } else if (flowName.includes("Free Recall")) {
                    timeout = globalSettings.frTimeout * 1000;
                } else if (flowName.includes("Cued Recall")) {
                    timeout = globalSettings.crTimeout * 1000;
                }

                console.log("Flow: ", flowName);
                console.log("Timeout: ", timeout);

                const controller = new AbortController();
                const reason = new DOMException("STT timeout", "TimeoutError");
                const timeoutId = setTimeout(() => controller.abort(reason), timeout);

                // Google fetch
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/promise-function-async
                const googleRequest = () =>
                    fetch(`https://speech.googleapis.com/v1p1beta1/speech:recognize?key=${googleApiKey}`, {
                        method: "POST",
                        body: JSON.stringify(data),
                        signal: controller.signal
                    });

                // Azure fetch
                // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/promise-function-async
                const azureRequest = () =>
                    fetch(
                        `https://${azureRegion}.stt.speech.microsoft.com/speech/recognition/conversation/cognitiveservices/v1?language=${languageCode}&format=detailed`,
                        {
                            method: "POST",
                            headers: {
                                "Ocp-Apim-Subscription-Key": azureApiKey,
                                "Content-Type": "audio/wav"
                            },
                            body: blob,
                            signal: controller.signal
                        }
                    );

                const [googleResponse, azureReponse] = await Promise.all([googleRequest(), azureRequest()]);

                clearTimeout(timeoutId);

                const googleJson = await googleResponse.json();
                const azureJson = await azureReponse.json();

                transaction.statusCode = googleResponse.status;
                transaction.statusText = googleResponse.statusText;
                transaction.responseStt = googleJson;
                transaction.azureResponseStt = azureJson;

                logAppInsight(transaction);

                const emptyResult = {
                    speechBlob: blob,
                    transcript: "-",
                    elapsed: Date.now() - start,
                    confidence: 0,
                    intendedWord,
                    context,
                    source: SttSourceType.Google
                };

                // Update results state with transcribed text
                if (isEmpty(googleJson.results) && azureJson.DisplayText === "") {
                    setResults([emptyResult]);
                } else {
                    const flattenAlternatives = flatten(googleJson?.results?.map((x: any) => x.alternatives)).filter(
                        (x) => !isEmpty(x)
                    );

                    const googleResult = flattenAlternatives.map((alternative: any) => ({
                        speechBlob: blob,
                        transcript: alternative.transcript.replace(".", ""),
                        elapsed: Date.now() - start,
                        confidence: alternative.confidence,
                        intendedWord,
                        context,
                        source: SttSourceType.Google
                    }));

                    const azureResult = {
                        speechBlob: blob,
                        transcript: flatten(azureJson.NBest?.map((x: { Lexical: string }) => x.Lexical))
                            .join(" ")
                            .trim(),
                        elapsed: Date.now() - start,
                        confidence: azureJson.NBest[0].Confidence ?? 0,
                        intendedWord,
                        context,
                        source: SttSourceType.Azure
                    };

                    if (
                        isEmpty(googleResult) &&
                        azureResult.transcript === "" &&
                        (context?.shouldEnd || !context?.stepName?.includes("Free Recall"))
                    ) {
                        setResults([emptyResult, azureResult]);
                    } else {
                        const gr = isEmpty(googleResult) ? [emptyResult] : googleResult;
                        setResults([...gr, azureResult]);
                    }
                }

                if (!reset) {
                    setIsRecording(false);
                }
            } catch (err: any) {
                console.error(err);
                if (err.message === "STT timeout") {
                    dispatch(updateInternetStatus({ status: InternetStatus.Timeout }));
                } else if (err.message === "Failed to fetch") {
                    dispatch(updateInternetStatus({ status: InternetStatus.Disconected }));
                }
            }
        };
    };

    const handleVolume = (): void => {
        if (!stream) return;

        const input = audioContextRef.current!.createMediaStreamSource(stream);
        const analyserNode = audioContextRef.current!.createAnalyser();

        analyserNode.smoothingTimeConstant = 0.8;
        analyserNode.fftSize = 1024;

        input.connect(analyserNode);
        setVol(0);

        let frame;
        const onFrame = (): void => {
            frame = undefined;
            const array = new Uint8Array(analyserNode.frequencyBinCount);
            analyserNode.getByteFrequencyData(array);
            const averageVolume = getAverageVolume(array);
            setVol(averageVolume);
            if (!frame) {
                frame = window.requestAnimationFrame(onFrame);
            }
        };

        frame = window.requestAnimationFrame(onFrame);
    };

    const stopMediaStream = (): void => {
        mediaStream.current?.getAudioTracks()[0]?.stop();
    };

    const handleDestroy = (): void => {
        stream?.getTracks()[0].stop();
        void recorder?.destroy();
    };

    function getAverageVolume(array: Uint8Array): number {
        let amplitudeSum = 0;

        for (let i = 0; i < array.length; i++) {
            amplitudeSum += array[i] * array[i];
        }

        return Math.sqrt(amplitudeSum / array.length) / 100;
    }

    const logAppInsight = (transaction: ITransaction): void => {
        const input = JSON.stringify(transaction);
        const splitNumber = 30000;
        const data = {
            assessmentId: transaction.assessmentId,
            assessmentFlow: transaction.assessmentFlow,
            statusCode: transaction.statusCode,
            statusText: transaction.statusText,
            id: uuidv4(),
            part: 0,
            text: ""
        };

        if (input.length <= splitNumber) {
            appInsights.trackTrace({
                message: input,
                severityLevel: SeverityLevel.Information
            });
        } else {
            for (let i = 0; i < input.length; i += splitNumber) {
                data.part++;
                data.text = input.slice(i, i + splitNumber);

                appInsights.trackTrace({
                    message: JSON.stringify(data),
                    severityLevel: SeverityLevel.Information
                });
            }
        }
    };

    return {
        error,
        isRecording,
        results,
        recordingVolume: vol,
        currentDecibels,
        stream,
        startSpeechToText,
        stopSpeechToText,
        destroy: handleDestroy
    };
};
